import styled from 'styled-components';
import { Box } from '@mantine/core';

/**
 * Outer padded shell for the expanded row. `MantineTableRow` renders the detail panel inside a
 * `Flex`, so without `flex: 1` this shrinks to its content and leaves the rest of the row blank.
 */
export const RowEditorContainer = styled(Box)`
	display: flex;
	flex: 1;
	min-width: 0;
	flex-direction: column;
	gap: 1.25rem;
	padding: 1rem;
`;

/**
 * The bordered card holding the settings. Figma SDW/S3b: a single 1px greys border with a
 * 0.25rem radius around the whole thing, so the section bands and the column divider read as
 * lines inside one card rather than as separate panels.
 */
export const EditorPanel = styled.div`
	background-color: ${({ theme }) => theme.v2.color.white[500]};
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
	border-radius: 0.25rem;
	overflow: hidden;
`;

/** Shared column geometry: two equal halves, collapsing to one when the panel is squeezed. */
const twoColumns = `
	display: grid;
	grid-template-columns: repeat(2, 1fr);

	@media (max-width: 640px) {
		grid-template-columns: 1fr;
	}
`;

/** Banded row carrying the "Document requirement" / "Personalised document" section titles. */
export const SectionHeaderRow = styled.div`
	${twoColumns}
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	border-bottom: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
`;

export const SectionHeaderCell = styled.div`
	padding: 0.5rem 1rem;

	& + & {
		border-left: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
	}

	@media (max-width: 640px) {
		& + & {
			border-left: none;
		}
	}
`;

/** The two-column "Document requirement" / "Personalised document" body, divided by a rule. */
export const ColumnsGrid = styled.div`
	${twoColumns}
`;

export const Column = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem;

	& + & {
		border-left: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
	}

	@media (max-width: 640px) {
		& + & {
			border-left: none;
			border-top: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
		}
	}
`;

/**
 * A second section inside a column — Delivery hanging off the bottom of Personalised document.
 * The negative margins bleed it past the column's padding so its header band spans the column
 * exactly like the one at the top of the panel, and its body supplies the column's bottom
 * padding in place of the one it swallows.
 */
export const ColumnSubSection = styled.div`
	display: flex;
	flex-direction: column;
	margin: 0 -1rem -1rem;
`;

/** The same cell as the panel's own section headers, with the ground and rules that row carries. */
export const ColumnSubSectionHeader = styled(SectionHeaderCell)`
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	border-top: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
	border-bottom: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
`;

export const ColumnSubSectionBody = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1rem;
	padding: 1rem;
`;

/**
 * The panel's own action band, under a rule like the section headers above it. Inside the card
 * rather than floating below it: once the tag picker moved into the column, a bar outside the
 * card was a button and a warning stranded in open space.
 */
export const FooterBar = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: flex-end;
	gap: 1rem;
	padding: 1rem;
	border-top: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};
`;

/** Mid-grey `gray[1]` in this theme is close in luminance to `gray[6]`, so `dimmed` text is
 *  invisible on it — callers must set an explicit text color (e.g. `c="dark.0"`) here. */
export const WarningBanner = styled.div`
	padding: 0.5rem 0.75rem;
	border-radius: 4px;
	background-color: ${({ theme }) => theme.v3.colors.gray[1]};
`;

/**
 * Figma indents the signature method under the checkbox it depends on, and keeps it visible but
 * greyed when signing is off rather than removing it. 1.75rem lines it up with the checkbox label
 * (1.25rem box + 0.5rem gap).
 */
export const DependentField = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: 0.25rem;
	/* Figma runs a rule down the nested group so it reads as hanging off the checkbox above.
	   The 0.625rem margin puts it under that checkbox's centre (half of Mantine's 1.25rem box),
	   and the padding makes up the rest of the 1.75rem the label text is indented by. */
	margin-left: 0.625rem;
	padding: 0.5rem 0.5rem 0.5rem 1.125rem;
	border-left: 1px solid ${({ theme }) => theme.v3.colors.gray[3]};

	/* Mantine leaves no space between a RadioGroup's label and its options, which reads as
	   squashed against the heading. */
	& .mantine-RadioGroup-label {
		margin-bottom: 0.5rem;
	}
`;
